import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { authStore } from "../util/authStore";
import {
  accountAuthError, changePassword, forgotPassword, getAccountSecurity,
  removeWhatsAppLogin, requestWhatsAppEnrollment, verifyWhatsAppEnrollment,
} from "../api/accountAuthApi";
import PasswordFields, { authButton, authInput, passwordError } from "../components/auth/PasswordFields";
import WhatsAppOtpForm from "../components/auth/WhatsAppOtpForm";

export default function AccountSecurityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [security, setSecurity] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [removalPassword, setRemovalPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setError("");
    try {
      const result = await getAccountSecurity();
      if (!result || typeof result.has_password !== "boolean" || typeof result.whatsapp_available !== "boolean") throw new Error("Invalid account security response.");
      setSecurity(result);
    } catch (err) { setError(accountAuthError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const signedOut = () => {
    authStore.logout();
    navigate("/login", { replace: true });
  };
  const savePassword = async (event) => {
    event.preventDefault();
    setError(""); setNotice("");
    const problem = passwordError(password, confirmation);
    if (problem) { setError(problem); return; }
    setBusy(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: password });
      signedOut();
    } catch (err) { setError(accountAuthError(err)); }
    finally { setBusy(false); }
  };
  return <main className="mx-auto max-w-2xl space-y-6 py-6">
    <h1 className="text-2xl font-bold">Account Security</h1>
    {user?.must_change_password && <p role="status" className="rounded-lg bg-amber-50 p-4 text-amber-900">You must update your password before accessing the application.</p>}
    {error && <div role="alert" className="text-rose-700">{error}{!security && <button className="ml-3 underline" onClick={load}>Retry</button>}</div>}
    {notice && <p role="status">{notice}</p>}
    {!security ? !error && <p role="status">Loading security settings...</p> : <>
      <section className="space-y-4 rounded-xl border bg-white p-6">
        <h2 className="text-lg font-bold">Change password</h2>
        {security.has_password ? <form className="space-y-4" onSubmit={savePassword}>
          <label className="block text-sm font-semibold">Current password
            <input type="password" autoComplete="current-password" className={authInput} required value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
          </label>
          <PasswordFields password={password} confirmation={confirmation} onPassword={setPassword} onConfirmation={setConfirmation} />
          <p className="text-sm text-slate-600">Changing your password signs out existing sessions. You will need to sign in again.</p>
          <button disabled={busy} className={authButton}>{busy ? "Saving..." : "Change password"}</button>
        </form> : <>
          <p className="text-sm text-slate-600">Set a password using a secure email link before managing password-based security settings.</p>
          <button className={authButton} disabled={busy} onClick={async () => {
            setBusy(true); setError("");
            try { await forgotPassword(user.email); setNotice("If eligible, a password setup/reset email will be sent to your account."); }
            catch (err) { setError(accountAuthError(err)); }
            finally { setBusy(false); }
          }}>Send password setup link</button>
        </>}
      </section>
      <section className="space-y-4 rounded-xl border bg-white p-6">
        <h2 className="text-lg font-bold">WhatsApp login</h2>
        {security.whatsapp_phone_e164 && <p>Registered number: <strong>{security.whatsapp_phone_e164}</strong> · {security.whatsapp_login_enabled ? "Enabled" : "Disabled"}</p>}
        {!security.whatsapp_available ? <p>WhatsApp login is not configured. Password login remains available.</p>
          : security.has_password && !user?.must_change_password ? <>
            <p className="text-sm text-slate-600">Verify a number you control. Registering or changing it signs out existing sessions.</p>
            <WhatsAppOtpForm enrollment request={requestWhatsAppEnrollment} verify={verifyWhatsAppEnrollment} onVerified={signedOut} />
          </> : <p>Complete password setup before enrolling a WhatsApp login number.</p>}
            {security.whatsapp_phone_e164 && security.has_password && !user?.must_change_password && <form className="space-y-3 border-t pt-4" onSubmit={async (event) => {
              event.preventDefault(); setBusy(true); setError("");
              try { await removeWhatsAppLogin(removalPassword); signedOut(); }
              catch (err) { setError(accountAuthError(err)); }
              finally { setBusy(false); }
            }}>
              <label className="block text-sm font-semibold">Current password to remove WhatsApp login
                <input required type="password" autoComplete="current-password" className={authInput} value={removalPassword} onChange={(event) => setRemovalPassword(event.target.value)} />
              </label>
              <button className={authButton} disabled={busy}>Remove WhatsApp login</button>
            </form>}
      </section>
    </>}
    {!user?.must_change_password && <Link className="inline-block text-cyan-800 underline" to="/agents">Back to Agents</Link>}
    <button type="button" className="ml-4 text-rose-700" onClick={signedOut}>Sign out</button>
  </main>;
}
